import React from 'react';
import {
  X, ArrowRight, Clock, CheckCircle2, AlertTriangle, Building2,
  ClipboardList, FileText, Calendar, Loader2, CircleCheck, CircleAlert,
} from 'lucide-react';

// ── Palette — same warm family as JobOrdersReviewPage / the Estimates flow.
const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const LINE   = 'rgba(43, 38, 32, 0.09)';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_MONO    = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

const SHOP_DOCS = [
  { key: 'cutlist',    label: 'Cut list' },
  { key: 'glassorder', label: 'Glass order' },
  { key: 'shoporder',  label: 'Shop order' },
  { key: 'flexscreen', label: 'FlexScreen order' },
];

function clockTime(ts) {
  if (!ts) return 'just now';
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Small state pill on the right of a pipeline row. */
function StatusChip({ tone, children }) {
  const map = {
    done:    { fg: MOSS,  bg: 'rgba(59, 107, 68, 0.10)' },
    warn:    { fg: OCHRE, bg: 'rgba(166, 117, 42, 0.12)' },
    error:   { fg: RUST,  bg: 'rgba(161, 61, 45, 0.12)' },
    working: { fg: STONE, bg: 'rgba(140, 130, 117, 0.14)' },
    idle:    { fg: STONE, bg: 'rgba(140, 130, 117, 0.10)' },
  };
  const c = map[tone] || map.idle;
  return (
    <span style={{
      flexShrink: 0, fontSize: '11px', fontWeight: 600, letterSpacing: '0.02em',
      color: c.fg, background: c.bg, borderRadius: '999px', padding: '3px 9px',
      fontFamily: FONT_SANS, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

/** One row in the pipeline list. */
function Row({ Icon, iconColor, title, subtitle, chip, children, last }) {
  return (
    <div style={{
      display: 'flex', gap: '12px', padding: '13px 0',
      borderBottom: last ? 'none' : `1px solid ${LINE}`,
    }}>
      <div style={{
        flexShrink: 0, width: '30px', height: '30px', borderRadius: '9px',
        background: 'rgba(43, 38, 32, 0.05)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} strokeWidth={2} color={iconColor || STONE} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: INK, fontFamily: FONT_SANS }}>{title}</span>
          {chip}
        </div>
        {subtitle && (
          <div style={{ fontSize: '12px', color: STONE, marginTop: '2px', lineHeight: 1.45, fontFamily: FONT_SANS }}>
            {subtitle}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default function JobApprovalModal({ successInfo, pdfError, docProgress, onClose, onNavigate }) {
  if (!successInfo) return null;

  const {
    contractNumber, customerName, windowCount = 0, doorCount = 0,
    hasWindows, cutlistJobId, qboSync, approvedBy, approvedAt,
  } = successInfo;

  const qboOk      = qboSync?.ok;
  const qboFailed  = qboSync && !qboSync.ok;
  const invoiceNum = qboSync?.quickbooksInvoiceNumber || qboSync?.quickbooksInvoiceId;

  // Shop-document rollup
  const dp        = docProgress || null;
  const docStates = dp ? SHOP_DOCS.map(d => dp[d.key] || 'working') : [];
  const docsReady = docStates.filter(s => s === 'ready').length;
  const docsError = docStates.some(s => s === 'error');
  const docsAllReady = dp && docsReady === SHOP_DOCS.length;

  // Overall tone drives the accent + eyebrow
  const attention = qboFailed || pdfError || docsError;
  const accent = pdfError || docsError ? RUST : qboFailed ? OCHRE : MOSS;

  // Smart next-step line
  let nextStep;
  if (pdfError) {
    nextStep = 'Resolve the glass / shop PDF issue below before sending the glass order.';
  } else if (qboFailed) {
    nextStep = `Open QuickBooks and confirm invoice ${invoiceNum ? `#${invoiceNum} ` : ''}posted for ${customerName || 'this customer'}.`;
  } else if (hasWindows && dp && !docsAllReady) {
    nextStep = 'Shop documents are still generating — they will be attached to the job automatically.';
  } else if (hasWindows) {
    nextStep = 'Review the cut list, then send the glass order to the supplier.';
  } else {
    nextStep = 'This job is in Unscheduled Jobs — it will be placed on the calendar next.';
  }

  const goPrimary = () => onNavigate(hasWindows ? `/cutlist/${cutlistJobId}/cutlist` : '/');

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(30, 25, 20, 0.62)',
        backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', fontFamily: FONT_SANS,
        animation: 'twpJamFade 0.2s ease-out',
      }}
    >
      <style>{`
        @keyframes twpJamFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes twpJamRise {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes twpJamSpin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '480px', maxHeight: 'calc(100vh - 40px)',
          overflowY: 'auto', background: PAPER, borderRadius: '20px',
          border: `1px solid ${LINE}`,
          boxShadow: '0 30px 80px rgba(30, 25, 20, 0.35), 0 2px 10px rgba(30, 25, 20, 0.12)',
          animation: 'twpJamRise 0.32s cubic-bezier(0.2, 0.9, 0.3, 1.05)',
        }}
      >
        {/* Accent strip */}
        <div style={{ height: '3px', background: accent }} />

        {/* Header */}
        <div style={{ padding: '20px 26px 16px', position: 'relative' }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: '14px', right: '14px',
              width: '30px', height: '30px', borderRadius: '8px',
              border: 'none', background: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: STONE,
            }}
          >
            <X size={17} strokeWidth={2} />
          </button>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '11px', fontWeight: 600, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: accent, marginBottom: '7px',
          }}>
            {attention
              ? <AlertTriangle size={13} strokeWidth={2.5} />
              : <CheckCircle2 size={13} strokeWidth={2.5} />}
            {attention ? 'Approved — needs a check' : 'Job order approved'}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: '25px', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>
              #{contractNumber || '—'}
            </span>
            <span style={{ fontSize: '14px', color: STONE }}>{customerName || 'Customer'}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '12px', color: STONE }}>
            <Clock size={12} strokeWidth={2} />
            <span>Approved {clockTime(approvedAt)}{approvedBy ? ` · ${approvedBy}` : ''}</span>
          </div>
        </div>

        {/* Smart next step */}
        <div style={{
          margin: '0 26px 4px', padding: '11px 13px',
          background: 'rgba(43, 38, 32, 0.04)', borderRadius: '11px',
          display: 'flex', gap: '9px', alignItems: 'flex-start',
        }}>
          <ArrowRight size={15} strokeWidth={2.5} color={accent} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '12.5px', color: INK, lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 600 }}>Next:</strong> {nextStep}
          </div>
        </div>

        {/* Pipeline */}
        <div style={{ padding: '4px 26px 8px' }}>
          <Row
            Icon={CheckCircle2}
            iconColor={MOSS}
            title="Job order approved"
            subtitle="Locked and moved to the Approved list."
            chip={<StatusChip tone="done">Done</StatusChip>}
          />

          <Row
            Icon={Building2}
            iconColor={qboFailed ? OCHRE : MOSS}
            title="QuickBooks invoice"
            subtitle={
              qboOk
                ? <>
                    {invoiceNum ? <>Invoice <span style={{ fontFamily: FONT_MONO }}>#{invoiceNum}</span> created.</> : 'Invoice created.'}
                    {qboSync?.alreadySynced && ' Was already synced earlier.'}
                    {qboSync?.customerWasCreated && ' New customer added to QuickBooks.'}
                  </>
                : 'Sync did not confirm — verify the invoice directly in QuickBooks.'
            }
            chip={qboOk
              ? <StatusChip tone="done">Synced</StatusChip>
              : <StatusChip tone="warn">Check QBO</StatusChip>}
          />

          <Row
            Icon={ClipboardList}
            iconColor={hasWindows ? MOSS : STONE}
            title="Cut list"
            subtitle={hasWindows
              ? `${windowCount} window${windowCount !== 1 ? 's' : ''} loaded for the shop.`
              : 'No TWP windows on this order — nothing to cut.'}
            chip={hasWindows
              ? <StatusChip tone="done">Created</StatusChip>
              : <StatusChip tone="idle">Not needed</StatusChip>}
          />

          {hasWindows && (
            <Row
              Icon={FileText}
              iconColor={docsError ? RUST : docsAllReady ? MOSS : STONE}
              title="Shop documents"
              subtitle={!dp ? 'Queued for generation.' : null}
              chip={
                !dp ? <StatusChip tone="idle">Queued</StatusChip>
                : docsError ? <StatusChip tone="error">Issue</StatusChip>
                : docsAllReady ? <StatusChip tone="done">Ready</StatusChip>
                : <StatusChip tone="working">{docsReady}/{SHOP_DOCS.length}</StatusChip>
              }
            >
              {dp && (
                <div style={{ marginTop: '8px', display: 'grid', gap: '5px' }}>
                  {SHOP_DOCS.map(d => {
                    const st = dp[d.key] || 'working';
                    return (
                      <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: STONE }}>
                        {st === 'ready'
                          ? <CircleCheck size={13} strokeWidth={2.2} color={MOSS} />
                          : st === 'error'
                            ? <CircleAlert size={13} strokeWidth={2.2} color={RUST} />
                            : <Loader2 size={13} strokeWidth={2.2} color={STONE} style={{ animation: 'twpJamSpin 0.9s linear infinite' }} />}
                        <span style={{ color: st === 'error' ? RUST : INK }}>{d.label}</span>
                        <span style={{ marginLeft: 'auto' }}>
                          {st === 'ready' ? 'Attached' : st === 'error' ? 'Failed' : 'Generating…'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Row>
          )}

          <Row
            Icon={Calendar}
            iconColor={OCHRE}
            title="Calendar"
            subtitle={
              <>Waiting for AJ to schedule
                {windowCount > 0 && ` · ${windowCount} window${windowCount !== 1 ? 's' : ''}`}
                {doorCount > 0 && ` · ${doorCount} door${doorCount !== 1 ? 's' : ''}`}.
              </>
            }
            chip={<StatusChip tone="warn">Unscheduled</StatusChip>}
            last
          />
        </div>

        {/* Callouts */}
        {(qboFailed || pdfError) && (
          <div style={{ padding: '0 26px 8px', display: 'grid', gap: '9px' }}>
            {qboFailed && (
              <div style={{
                padding: '11px 13px', borderRadius: '11px',
                background: 'rgba(166, 117, 42, 0.08)', border: `1px solid rgba(166, 117, 42, 0.25)`,
              }}>
                <div style={{ display: 'flex', gap: '7px', alignItems: 'center', fontSize: '12.5px', fontWeight: 600, color: OCHRE }}>
                  <AlertTriangle size={13} strokeWidth={2.5} /> Check QuickBooks before re-syncing
                </div>
                <div style={{ fontSize: '12px', color: INK, lineHeight: 1.5, marginTop: '5px' }}>
                  Reason: {qboSync.error || qboSync.message || 'Unknown error'}.
                </div>
                <div style={{ fontSize: '11.5px', color: STONE, lineHeight: 1.5, marginTop: '5px' }}>
                  The approval still went through. If the invoice <strong>is</strong> in QuickBooks it actually
                  synced (the UI just lost the confirmation). If it is <strong>not</strong> there, move this
                  job back to Pending Review and approve again.
                </div>
              </div>
            )}
            {pdfError && (
              <div style={{
                padding: '11px 13px', borderRadius: '11px',
                background: 'rgba(161, 61, 45, 0.07)', border: `1px solid rgba(161, 61, 45, 0.25)`,
              }}>
                <div style={{ display: 'flex', gap: '7px', alignItems: 'center', fontSize: '12.5px', fontWeight: 600, color: RUST }}>
                  <AlertTriangle size={13} strokeWidth={2.5} /> Glass / shop PDF issue
                </div>
                <div style={{ fontSize: '12px', color: INK, lineHeight: 1.5, marginTop: '5px', whiteSpace: 'pre-line' }}>
                  {pdfError}
                </div>
                <div style={{ fontSize: '11.5px', color: STONE, lineHeight: 1.5, marginTop: '5px' }}>
                  Do not send the glass order until this is resolved. Report to Kwizera.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '14px 26px 20px', borderTop: `1px solid ${LINE}`,
          background: 'rgba(43, 38, 32, 0.025)',
          display: 'flex', gap: '9px',
        }}>
          <button
            onClick={goPrimary}
            style={{
              flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              padding: '12px 16px', borderRadius: '11px', border: 'none', cursor: 'pointer',
              background: WALNUT, color: '#fff', fontSize: '13px', fontWeight: 600,
              fontFamily: FONT_SANS, boxShadow: '0 4px 14px rgba(139, 69, 19, 0.28)',
            }}
          >
            {hasWindows ? 'Go to cut list' : 'View calendar'}
            <ArrowRight size={15} strokeWidth={2.5} />
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: '11px', cursor: 'pointer',
              background: '#fff', color: STONE, border: `1.5px solid ${LINE}`,
              fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
